import { z } from 'zod'
import { Currency } from '../../../enums/currency.enum'

// upper bound on the [from, to] window so a malformed request can't force
// the report to scan an unbounded range of deposits. mirrors the cap
// applied to expenses-by-category for symmetry.
const MAX_RANGE_DAYS = 36 * 31

// `from` / `to` are inclusive ISO dates that scope which deposits appear in
// the report: a deposit is included when its [openDate, maturityDate] term
// overlaps the [from, to] range. they're independent of `year`, which
// scopes only the right-side monthly cells and the year-tagged top totals.
export const depositsReportSummaryQuerySchema = z
  .object({
    currency: z.nativeEnum(Currency),
    year: z.coerce.number().int().min(2000).max(2100),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'from must be YYYY-MM-DD'),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'to must be YYYY-MM-DD'),
    showMatured: z
      .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
      .optional()
      .default(false),
  })
  .superRefine((value, ctx) => {
    const fromMs = Date.parse(`${value.from}T00:00:00.000Z`)
    const toMs = Date.parse(`${value.to}T00:00:00.000Z`)
    if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return
    if (toMs < fromMs) {
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: 'to must be on or after from',
      })
      return
    }
    const days = Math.round((toMs - fromMs) / (1000 * 60 * 60 * 24))
    if (days > MAX_RANGE_DAYS) {
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: 'date range is limited to 36 months',
      })
    }
  })

export type DepositsReportSummaryQueryDto = z.infer<typeof depositsReportSummaryQuerySchema>
